<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { SavedRepliesMenu } from '@dashhudson/dashing-ui/saved-replies-menu';
import { useDashToast as useToast } from '@dashhudson/dashing-ui/toast';
import { useConfirmationDialog } from '@dashhudson/dashing-ui/confirmation-dialog';
import {
  COMMUNITY_INTERACTION_PLATFORM_BY_TYPE,
  COMMUNITY_INTERACTION_TYPE_DATA,
  SAVED_REPLY_CONFIRMATION,
} from '@/app/community/constants';
import { useCommunityStore } from '@/stores/community';
import { useTrackingStore } from '@/stores/tracking';
import { getBrandHandleFromInteraction } from '@/app/community/utils/mixpanel-events';
import { getFollowerHandleFromInteraction } from '@/app/community/utils/community-interactions';
import { hasMatchInActions } from '@/app/community/utils/interaction-rules';
import { REPLY_USED_IN_AUTOMATION_RULE_TOOLTIP } from '@/app/community/constants/inboxAutomation';
import { useCommunityInteractionRulesStore } from '@/stores/community-interaction-rules';

const props = defineProps({
  shown: Boolean,
  interaction: { type: Object, required: true },
  dropdown: { type: Object, default: undefined },
});

const emits = defineEmits(['select', 'update:shown']);

const communityStore = useCommunityStore();
const trackingStore = useTrackingStore();
const ruleStore = useCommunityInteractionRulesStore();

const { addToast } = useToast({ life: 3000 });
const { showConfirmationDialog } = useConfirmationDialog({
  group: 'global',
  acceptSeverity: 'danger',
  onAccept: null,
  onReject: null,
});

const internalShown = computed({
  get: () => props.shown,
  set: (val) => emits('update:shown', val),
});
const loaded = ref(false);

const platform = computed(() => COMMUNITY_INTERACTION_TYPE_DATA[props.interaction.type] ?? {});
const title = computed(() =>
  platform.value.label ? `Saved ${platform.value.label} Replies` : undefined,
);

const deleteMenuOptionProps = (reply) => {
  const brandRules = ruleStore.interactionRulesByBrand?.[props.interaction.brandId] ?? [];
  const usedByRules =
    brandRules.length > 0 && hasMatchInActions(brandRules, 'presetReplyId', reply.id);
  if (usedByRules) {
    return {
      deleteMenuOptionProps: { disabled: true, tooltip: REPLY_USED_IN_AUTOMATION_RULE_TOOLTIP },
    };
  }
  return {};
};

const savedReplies = computed(() =>
  communityStore.presetReplyList
    .map((data) => {
      const { message, ...replyData } = data;
      return { ...replyData, reply: message, ...deleteMenuOptionProps(replyData) };
    })
    .sort((a, b) => {
      const titleA = (a.title || '').toLowerCase();
      const titleB = (b.title || '').toLowerCase();
      return titleA.localeCompare(titleB);
    }),
);
const payload = computed(() => ({
  brandId: props.interaction.brandId,
  type: platform.value.platform,
}));
const mixpanelEventProps = computed(() => {
  if (props.interaction?.type) {
    const { platform: interactionPlatform, messageType } =
      COMMUNITY_INTERACTION_PLATFORM_BY_TYPE[props.interaction.type] ?? {};
    return {
      brandHandle: getBrandHandleFromInteraction(props.interaction),
      platform: interactionPlatform,
      messageType,
      followerHandle: getFollowerHandleFromInteraction(props.interaction),
      mediaId: props.interaction?.mediaId,
      mailBox: props.interaction?.isArchived ? 'Archive' : 'Inbox',
    };
  }
  return {};
});

function handleMixpanel(reply, action) {
  trackingStore.track('Community Preset Replies', {
    ...mixpanelEventProps.value,
    action,
    title: reply.title,
    presetId: reply.id,
  });
}

async function fetchSavedReplies() {
  try {
    await communityStore.getPresetReplyList(payload.value.brandId, payload.value.type);
  } catch {
    addToast({ message: 'Failed to load preset replies', severity: 'error' });
  }
}

async function onAdd(reply) {
  let newPresetReply;
  try {
    const res = await communityStore.createPresetReply({
      data: {
        ...payload.value,
        message: reply.reply,
        title: reply.title,
      },
    });
    newPresetReply = res.data?.data;
  } catch {
    addToast({ message: 'Failed to save your preset reply. Please try again.', severity: 'error' });
    return;
  }
  handleMixpanel(newPresetReply, 'Created');
}

async function onEdit(reply) {
  try {
    await communityStore.updatePresetReply({
      replyId: reply.id,
      data: {
        message: reply.reply,
        title: reply.title,
      },
    });
  } catch {
    addToast({ message: 'Failed to save your preset reply. Please try again.', severity: 'error' });
    return;
  }
  handleMixpanel(reply, 'Edited');
}

async function onRemove(reply) {
  const confirmed = await showConfirmationDialog(SAVED_REPLY_CONFIRMATION);
  if (confirmed) {
    try {
      await communityStore.deletePresetReply(reply.id);
    } catch {
      addToast({ message: 'Failed to delete reply. Please try again.', severity: 'error' });
      return;
    }
    handleMixpanel(reply, 'Deleted');
  }
}

function onReplyClick(reply) {
  emits('select', reply.reply);
  trackingStore.track(`Community Preset Replies Selected`, {
    title: reply.title,
    presetId: reply.id,
    ...mixpanelEventProps.value,
  });
}

async function onBeforeClose(_reason, context) {
  if (context.hasChanges) {
    const confirmed = await showConfirmationDialog({
      header: 'Discard changes?',
      message: 'If you leave now, any unsaved changes will be lost.',
      acceptLabel: 'Discard',
      rejectLabel: 'Cancel',
    });
    return confirmed;
  }
  return true;
}
watch(
  () => internalShown.value,
  async (to) => {
    if (to && !loaded.value) {
      await fetchSavedReplies();
      loaded.value = true;
    }
  },
);

onMounted(async () => {
  await communityStore.clearPresetReplyList();
});
</script>

<template>
  <SavedRepliesMenu
    v-model:shown="internalShown"
    :title="title"
    :dropdown="dropdown"
    :replies="savedReplies"
    :add-new-callback="onAdd"
    :edit-callback="onEdit"
    :remove-callback="onRemove"
    :on-before-close="onBeforeClose"
    @reply-click="onReplyClick"
  />
</template>
